import { PageHero } from "@/components/PageHero";
import { ASSETS } from "@/lib/assets";
import { SEO } from "@/components/SEO";

const VALUES = [
  {
    title: "Ejerskab",
    body: "Vi tager opgaven alvorligt fra start. Ikke kun det, vi selv udfører, men hele forløbet frem til en løsning, der holder.",
  },
  {
    title: "Præcision",
    body: "Vi planlægger grundigt, arbejder systematisk og sørger for, at kvalitetssikring og dokumentation følger med hele vejen.",
  },
  {
    title: "Åbenhed",
    body: "Vi tror på klare aftaler, ærlig dialog og hurtige svar. Når virkeligheden ændrer sig, siger vi det, som det er.",
  },
  {
    title: "Driftssikkerhed",
    body: "Vi bygger løsninger til hverdagen efter afleveringen. Det skal ikke bare virke nu, men blive ved med at fungere.",
  },
];

export const Om = () => {
  return (
    <>
      <SEO
        title="Om Farum Kloak – Elitehold med 10+ års erfaring"
        description="Mød holdet bag Farum Kloak. Autoriseret kloakmester og certificeret rottebekæmper med base i Nordsjælland – med fokus på ejerskab, præcision og åben dialog."
      />
      <PageHero
        eyebrow="Om Farum Kloak"
        title={
          <>
            Et elitehold med <em className="text-[color:var(--fk-gold)]">10 års erfaring</em> i kloak.
          </>
        }
        subtitle="Farum Kloak er autoriseret kloakmester og certificeret rottebekæmper med base i Nordsjælland. Vi løser opgaver for private, erhverv og professionelle samarbejdspartnere i hele Storkøbenhavn."
        image={ASSETS.images.industrialPipes}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="eyebrow mb-6">Historien</div>
          </div>
          <div className="md:col-span-7">
            <p className="font-display text-2xl md:text-3xl leading-snug italic text-white/95">
              Farum Kloak blev bygget på et enkelt princip: At gøre arbejdet
              ordentligt og stå ved sit ord.
            </p>
            <p className="mt-8 text-white/70 font-light leading-relaxed">
              Siden da er virksomheden vokset til et fasttømret hold med
              autorisation, certificeringer og en bred faglighed inden for kloak,
              rottebekæmpelse, spuling, TV-inspektion og service. Vi løser både
              de akutte opgaver, hvor der skal handles hurtigt, og de større
              projekter, hvor planlægning, dokumentation og samarbejde skal gå op
              i en højere enhed.
            </p>
            <p className="mt-6 text-white/70 font-light leading-relaxed">
              For os handler et godt samarbejde ikke kun om at løse opgaven. Det
              handler om at møde op, være til at få fat i og levere et resultat,
              vi kan stå inde for.
            </p>
          </div>
        </div>
      </section>

      {/* Big team image */}
      <section
        data-testid="about-team-image"
        className="border-y border-white/10"
      >
        <img
          src={ASSETS.images.teamGroup}
          alt="Hele holdet ved Farum Kloak"
          className="w-full h-[60vh] md:h-[80vh] object-cover"
        />
      </section>

      {/* Staff portraits gallery */}
      <section
        data-testid="about-staff-gallery"
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">Holdet</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Folkene bag <em className="text-[color:var(--fk-gold)]">fagligheden.</em>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-white/60 font-light">
              Farum Kloak består af kloakmestre, rottefængere og folk med
              hænderne rigtigt skruet på. Vi kender vores fag, hjælper hinanden
              i marken og går efter løsningen, også når opgaven driller.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {ASSETS.staff.map((s, i) => (
            <figure
              key={s.id}
              data-testid={`staff-card-${i}`}
              className="group relative overflow-hidden border border-white/10 bg-[color:var(--fk-bg-2)]"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={s.photo}
                  alt={s.name}
                  className="w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 transition-all duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-black via-black/80 to-transparent pt-12">
                <div className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-[color:var(--fk-gold)] mb-1">
                  {s.role}
                </div>
                <div className="font-display text-lg md:text-xl text-white leading-tight">
                  {s.name}
                </div>
                {s.credentials && s.credentials.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {s.credentials.map((c) => (
                      <li
                        key={c}
                        className="text-[10px] md:text-[11px] text-white/65 font-light leading-snug"
                      >
                        · {c}
                      </li>
                    ))}
                  </ul>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-white/10 bg-[color:var(--fk-bg-2)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="eyebrow mb-4">Værdier</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                Det, vi <em className="text-[color:var(--fk-gold)]">står for.</em>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {VALUES.map((v, i) => (
              <div key={i} className="bg-[color:var(--fk-bg-2)] p-10 md:p-14">
                <div className="font-display text-3xl text-[color:var(--fk-gold)] mb-6">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-2xl md:text-3xl mb-4">
                  {v.title}
                </h3>
                <p className="text-white/60 font-light">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Base / HQ — fleet at base */}
      <section
        data-testid="about-base"
        className="relative border-t border-white/10 overflow-hidden"
      >
        <div
          className="relative h-[60vh] md:h-[85vh] bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.grabs.fleetAtBase})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/40" />
          <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24">
            <div className="max-w-xl">
              <div className="eyebrow mb-6">Hjemmebasen</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.05]">
                Herfra rykker <em className="text-[color:var(--fk-gold)]">holdet ud.</em>
              </h2>
              <p className="mt-6 text-white/75 font-light">
                Fra vores base i Nordsjælland planlægger vi dagens opgaver,
                gør bilerne klar og sender holdet ud til kunder i hele
                Storkøbenhavn og resten af Sjælland.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifikater */}
      <section className="border-t border-white/10 bg-[color:var(--fk-bg-2)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
            <div className="md:col-span-4">
              <div className="eyebrow mb-6">Dokumenteret kvalitet</div>
              <a
                href="https://byggekvalitet.dk"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="om-bkk-certificate"
                aria-label="Certificeret af Byggeriets Kvalitetskontrol — Kloak"
                className="inline-block group"
              >
                <img
                  src="/certifications/byggeriets-kvalitetskontrol-kloak.png"
                  alt="Certificeret af Byggeriets Kvalitetskontrol – Kloak"
                  width="176"
                  height="203"
                  loading="lazy"
                  className="w-40 md:w-44 h-auto opacity-95 group-hover:opacity-100 transition-opacity"
                />
              </a>
              <p className="mt-6 text-white/60 font-light text-sm leading-relaxed max-w-[300px]">
                Farum Kloak er certificeret inden for kloak af Byggeriets
                Kvalitetskontrol. Certificeringen understøtter vores fokus på
                dokumenteret kvalitet og korrekt udførelse.
              </p>
            </div>
            <div className="md:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                <div>
                  <div className="eyebrow mb-3">01</div>
                  <h3 className="font-display text-2xl mb-3">Aut. kloakmester</h3>
                  <p className="text-white/60 font-light text-sm">
                    Vi udfører autoriseret kloakarbejde med styr på kvalitetssikring,
                    dokumentation og gældende krav.
                  </p>
                </div>
                <div>
                  <div className="eyebrow mb-3">02</div>
                  <h3 className="font-display text-2xl mb-3">Cert. rottefænger</h3>
                  <p className="text-white/60 font-light text-sm">
                    Vi lokaliserer, bekæmper og sikrer mod rotter — både i bygningen
                    og i kloaksystemet.
                  </p>
                </div>
                <div>
                  <div className="eyebrow mb-3">03</div>
                  <h3 className="font-display text-2xl mb-3">Døgnvagt</h3>
                  <p className="text-white/60 font-light text-sm">
                    Når opgaven ikke kan vente, tager vi telefonen og rykker ud —
                    også uden for normal åbningstid.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
